import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Braces,
  Check,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { cn } from '../../../../../lib/utils';
import { useMetaInfo, useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import {
  useJsonStructureAggregatorStore,
  aggregateToPaths,
  keyId,
  type ColumnKey,
} from '../../stores/jsonStructureAggregatorStore';
import {
  useJsonStructureFilteredAggregatorStore,
  filterSignature,
} from '../../stores/jsonStructureFilteredAggregatorStore';
import { useAnalytics } from '../../../../../core/analytics';
import {
  buildJsonScanSql,
  JSON_SERVER_SCAN_LIMIT,
} from '../../utils/jsonScanSql';
import { openDeclareEnumColumnDialog } from './declareEnumColumnDialogStore';
import { openEnumDetailsDialog } from '../meta-info/enumDetailsDialogStore';
import { PathEnumGlyph } from '../meta-info/PathEnumGlyph';
import { findWithSchemaFold, schemaKeyMatches } from '../../utils/metaInfoSchemaMatch';
import { openJsonInspectorDialog } from '../meta-info/jsonInspectorDialogStore';
import { nameSuggestsEnum } from '../../utils/enumColumnCategorization';
import { findEnumDeclarationForPath } from '../../utils/pathEnumLookup';
import type { EnumColumnDeclaration } from '@/types/electron';
import { executeQueryV2 } from '../../../../database-objects/services/queryExecutor';
import { createExecutionId } from '../../../../../types/queryExecution';
import { looksLikeJsonString } from '../../utils/jsonDetection';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { useTableMetadata } from '../../../table-explorer/hooks/useTableMetadata';
import {
  JSON_PATH_HINT_TEXT,
  type JsonPathHint,
} from './jsonFilterFieldsValidator';
import {
  buildPathTree,
  parsePathSegments,
  collapseArrayWildcards,
  TYPE_ICONS,
  TYPE_LABELS,
  jsonPercentFormatter as percentFormatter,
  type PathTreeNode,
} from './jsonPathTree';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type {
  JsonStructureCachePath,
  JsonStructureCachePathType,
} from '@/types/electron';
import type { FilterGroup } from '../../../core/types/workspaceTypes';

// Re-export for back-compat — historically these lived in this file. The
// real definitions now sit in `./jsonPathTree` so they can be shared with
// JsonShapePreview. Keeping the re-exports lets any external callers (and
// the test file) continue importing from JsonPeekPopover unchanged.
export {
  parsePathSegments,
  buildPathTree,
  collapseArrayWildcards,
  type PathTreeNode,
};

type JsonPeekPopoverProps = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  declaredOnText: boolean;
  /**
   * Applied Table Explorer filters (minus this chip's own group) to scope
   * the popover's sources/scan to — filter-aware JSON peek (Path A).
   * Threaded through in this task; behavior lands in a follow-up task.
   */
  scanFilters?: FilterGroup[];
  /** Current JSON path value — drives the trigger label and freetext input. */
  pathValue: string;
  /** Validator hint for the current path; renders as inline error when set. */
  pathHint: JsonPathHint | null;
  /** Auto-open the popover on mount and focus the freetext input. */
  autoFocusPath?: boolean;
  /** Updates the path on freetext typing OR list pick. */
  onPathChange: (path: string) => void;
  /**
   * Fires *in addition to* `onPathChange` when the user picks a row from
   * the discovered-path tree. Carries the observed leaf type so the
   * caller can refine its sub-operator default ("string → contains",
   * "array → any =", …). Free-text typing does NOT fire this hook.
   */
  onTreePick?: (path: string, type: JsonStructureCachePathType | undefined) => void;
  /**
   * Fires when the user clicks one of the column-level pills above the
   * tree (`is valid JSON`, `is not valid JSON`, `is empty`,
   * `is not empty`). The chip is expected to clear `path` and stamp the
   * sub-op accordingly.
   */
  onPickDocumentScope?: (
    sub: 'is_valid' | 'is_not_valid' | 'is_empty' | 'is_not_empty',
  ) => void;
  /**
   * Filter scope this picker represents. `'path'` is the default — the
   * trigger displays the typed/picked JSONPath. `'whole-column'` swaps
   * the trigger label to "whole column" with a `{}` icon and marks the
   * Peek popover's footer link as current. The chip drives the value
   * via `JsonFilterFields` based on whether the active sub-op is
   * document-level (`is_valid` / `is_not_valid` / `is_empty`).
   */
  scope?: 'path' | 'whole-column';
  /**
   * Fires after a list-pick + the popover closes. Lets the parent move
   * focus forward — typically into the value input or onto the Apply
   * button — so picking a path keeps the keyboard flow uninterrupted.
   */
  onPickPathComplete?: () => void;
  /** Disable the trigger (e.g. while a confirmation dialog is open). */
  disabled?: boolean;
  /**
   * When true (default), the popover content renders the "use [*] to match
   * any array element" tip below the freetext input. Hosts where the
   * wildcard hint isn't relevant — e.g. the loose-FK target picker, which
   * binds a single scalar path — pass `false` to suppress it.
   */
  showWildcardHint?: boolean;
  /** Override the trigger button's `data-testid`. Defaults to `json-peek-button`. */
  triggerTestId?: string;
  /** Override the freetext path input's `data-testid`. Defaults to `json-path-input`. */
  inputTestId?: string;
  /** Forwarded as the trigger button's `id`, so callers can pair a `<label htmlFor>`. */
  triggerId?: string;
  /**
   * Optional Recheck affordance for the MSSQL low-compat wildcard hint —
   * when `pathHint === 'mssql-low-compat-wildcard'`, renders a tiny inline
   * "Recheck" button beside the message. Caller supplies the callback +
   * loading flag; absent on hosts (e.g. loose-FK target picker) where the
   * compat workflow doesn't apply.
   */
  onRecheckCompat?: () => void;
  recheckCompatFetching?: boolean;
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

type PeekCtx = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  /** Enum-column declarations from MetaInfo for this `(connection, database)`.
   * Each row gates the inline glyph it renders (declared / eligible / none). */
  enumColumns: ReadonlyArray<EnumColumnDeclaration>;
};

type Source =
  | 'cached'
  | 'live'
  | 'empty'
  | 'filtered-live'
  | 'filtered-empty';

type ScanState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'error'; message: string };

/**
 * "Peek" path-discovery popover that doubles as the JSON path input.
 *
 * The trigger renders as an input-styled combobox (showing the current
 * path value); clicking it opens the popover. Inside, the user can:
 *   1. Type a path freehand into the input at the top.
 *   2. Pick one of the discovered paths shown below.
 *
 * Order of preference for the path tree:
 *   1. `jsonStructureCache` entry from MetaInfo, if `sampledAt` is fresh
 *      (≤ 7 days). Renders instantly.
 *   2. The in-memory `jsonStructureAggregatorStore` aggregate (populated
 *      passively by slice 5 as the user browses).
 *   3. Empty state — user can run a server scan.
 *
 * "Scan from server" runs a fresh `SELECT col FROM ... LIMIT 1000` query
 * (with engine-appropriate JSON-validity guard for declared-on-text
 * columns), feeds the parsed values into the aggregator (which schedules
 * its debounced flush back to MetaInfo), and refreshes the popover view.
 */
export function JsonPeekPopover({
  connectionId,
  database,
  schema,
  table,
  column,
  engine,
  declaredOnText,
  scanFilters,
  pathValue,
  pathHint,
  autoFocusPath,
  onPathChange,
  onTreePick,
  onPickDocumentScope,
  onPickPathComplete,
  disabled,
  scope = 'path',
  showWildcardHint = true,
  triggerTestId = 'json-peek-button',
  inputTestId = 'json-path-input',
  triggerId,
  onRecheckCompat,
  recheckCompatFetching = false,
}: JsonPeekPopoverProps) {
  const [open, setOpen] = useState<boolean>(autoFocusPath === true);
  const [scanState, setScanState] = useState<ScanState>({ kind: 'idle' });
  const { track } = useAnalytics();
  const freetextInputRef = useRef<HTMLInputElement>(null);
  const pathMalformed = pathHint !== null;

  // Filter-aware peek (Path A). When Table Explorer filters are active, the
  // popover scopes its discovered paths + server scan to the rows matching
  // those filters, so polymorphic columns (e.g. a `payload` discriminated by
  // `event_type`) surface the variant's shape instead of a silently-mixed
  // union. `scanScope` re-syncs to the filter state but can be toggled to
  // escape back to the whole-column view.
  const filtersActive = (scanFilters?.length ?? 0) > 0;
  const scanSignature = useMemo(
    () => (filtersActive && scanFilters ? filterSignature(scanFilters) : null),
    [filtersActive, scanFilters],
  );
  const [scanScope, setScanScope] = useState<'filtered' | 'whole-column'>(
    filtersActive ? 'filtered' : 'whole-column',
  );
  useEffect(() => {
    setScanScope(filtersActive ? 'filtered' : 'whole-column');
  }, [filtersActive]);
  const filteredScopeActive =
    filtersActive && scanScope === 'filtered' && scanSignature !== null;

  const metaInfoQuery = useMetaInfo(connectionId, database);
  const invalidateMetaInfo = useInvalidateMetaInfo();
  const aggregates = useJsonStructureAggregatorStore((s) => s.aggregates);
  const pushSample = useJsonStructureAggregatorStore((s) => s.pushSample);
  const flushNow = useJsonStructureAggregatorStore((s) => s.flushNow);
  const filteredAggregates = useJsonStructureFilteredAggregatorStore(
    (s) => s.filteredAggregates,
  );
  const pushFilteredSample = useJsonStructureFilteredAggregatorStore(
    (s) => s.pushFilteredSample,
  );

  const columnKey: ColumnKey = useMemo(
    () => ({ connectionId, database, schema, table, column }),
    [connectionId, database, schema, table, column],
  );

  // Pull PK columns so the server-scan can be ORDER BY'd deterministically
  // (#14). Only fetch when the popover is open to avoid extra metadata
  // queries on every filter chip render. Composite PKs are supported —
  // every PK column joins into the ORDER BY (M2 in REVIEW_FINDINGS_PASS_2).
  const { primaryKeyColumns, metadata: tableMetadata, isLoading: metadataLoading } = useTableMetadata({
    connectionId,
    database,
    schema,
    tableName: table,
    enabled: open,
  });
  const pkColumns = useMemo<string[]>(
    () => Array.from(primaryKeyColumns),
    [primaryKeyColumns],
  );
  // The same metadata gives the filtered scan's WHERE the column types, so a
  // date filter takes the engine's temporal literal as in Table Explorer
  // (Oracle TO_DATE, SQL Server's seconds).
  const scanColumnTypeOf = useMemo(() => {
    const types = new Map((tableMetadata?.columns ?? []).map((c) => [c.name, c.dataType]));
    return types.size > 0 ? (name: string) => types.get(name) : undefined;
  }, [tableMetadata]);
  // L2 (Slice 10) — once metadata is loaded, surface "(no order)" when the
  // table has no primary key. Without a PK the scan can't ORDER BY anything,
  // so the LIMIT 1000 sample set is non-deterministic and prevalence numbers
  // may flip between scans. Suppress while metadata is still loading to
  // avoid a flicker on first open.
  const noOrderHint = !metadataLoading && pkColumns.length === 0;

  const cacheEntry = useMemo(() => {
    const list = metaInfoQuery.data?.jsonStructureCache ?? [];
    return findWithSchemaFold(
      list,
      schema,
      (e) => e.table === table && e.column === column,
    );
  }, [metaInfoQuery.data, schema, table, column]);

  // Enum-column declarations scoped to this `(schema, table, column)`. Used by
  // PeekPathRow to decide which inline glyph to render next to a leaf name.
  const enumColumnsForColumn = useMemo(() => {
    const list = metaInfoQuery.data?.enumColumns ?? [];
    return list.filter(
      (d) => schemaKeyMatches(d.schema, schema) && d.table === table && d.column === column,
    );
  }, [metaInfoQuery.data, schema, table, column]);

  const inMemoryPaths = useMemo(() => {
    const agg = aggregates[keyId(columnKey)];
    if (!agg || agg.pathTotal === 0) return null;
    // `aggregateToPaths` is the shared projection reused by the filtered
    // store; `sampledAt: null` marks this as a live (non-persisted) view.
    // Note: `paths` may be empty here when every sampled row was a root-level
    // array (or primitive). `sampledRows > 0 && paths.length === 0` is the
    // signal we use below to render the root-array empty state (#15).
    return { ...aggregateToPaths(agg), sampledAt: null as string | null };
  }, [aggregates, columnKey]);

  // Filtered bucket — only eligible when the popover is scoped to the active
  // filters AND a bucket exists whose signature matches the current filter
  // set. A signature mismatch (stale bucket from a prior filter) or an empty
  // bucket yields null → the source memo renders `filtered-empty`. The
  // persisted cache and whole-column live aggregate are intentionally NOT
  // consulted in filtered scope (silent variant-mixing is the bug this fixes).
  const filteredInMemoryPaths = useMemo(() => {
    if (!filteredScopeActive) return null;
    const agg = filteredAggregates[keyId(columnKey)];
    if (!agg || agg.signature !== scanSignature || agg.pathTotal === 0) {
      return null;
    }
    return aggregateToPaths(agg);
  }, [filteredScopeActive, filteredAggregates, columnKey, scanSignature]);

  const { source, paths, sampledRows, sampledAt } = useMemo<{
    source: Source;
    paths: JsonStructureCachePath[];
    sampledRows: number;
    sampledAt: string | null;
  }>(() => {
    // Filter-aware scope short-circuits the whole-column chain entirely: in
    // filtered scope we ONLY surface the matching filtered bucket, never the
    // persisted cache or the whole-column live aggregate.
    if (filteredScopeActive) {
      if (filteredInMemoryPaths) {
        return {
          source: 'filtered-live',
          paths: filteredInMemoryPaths.paths,
          sampledRows: filteredInMemoryPaths.sampledRows,
          sampledAt: null,
        };
      }
      return { source: 'filtered-empty', paths: [], sampledRows: 0, sampledAt: null };
    }
    const sortByPrevalence = (list: JsonStructureCachePath[]) =>
      [...list].sort(
        (a, b) => b.prevalence - a.prevalence || a.path.localeCompare(b.path),
      );
    const cacheFresh =
      cacheEntry !== undefined &&
      Date.now() - new Date(cacheEntry.sampledAt).getTime() <= SEVEN_DAYS_MS;
    // #23 — when both fresh cache and live aggregate exist, prefer whichever
    // has more samples. Otherwise the user can do a server scan that
    // produces 1000 rows but the popover still shows the older 50-sample
    // cache until invalidation lands.
    if (cacheFresh && cacheEntry && inMemoryPaths) {
      if (inMemoryPaths.sampledRows > cacheEntry.sampledRows) {
        return {
          source: 'live',
          paths: inMemoryPaths.paths,
          sampledRows: inMemoryPaths.sampledRows,
          sampledAt: inMemoryPaths.sampledAt,
        };
      }
      return {
        source: 'cached',
        paths: sortByPrevalence(cacheEntry.paths),
        sampledRows: cacheEntry.sampledRows,
        sampledAt: cacheEntry.sampledAt,
      };
    }
    if (cacheFresh && cacheEntry) {
      return {
        source: 'cached',
        paths: sortByPrevalence(cacheEntry.paths),
        sampledRows: cacheEntry.sampledRows,
        sampledAt: cacheEntry.sampledAt,
      };
    }
    if (inMemoryPaths) {
      return {
        source: 'live',
        paths: inMemoryPaths.paths,
        sampledRows: inMemoryPaths.sampledRows,
        sampledAt: inMemoryPaths.sampledAt,
      };
    }
    if (cacheEntry) {
      return {
        source: 'cached',
        paths: sortByPrevalence(cacheEntry.paths),
        sampledRows: cacheEntry.sampledRows,
        sampledAt: cacheEntry.sampledAt,
      };
    }
    return { source: 'empty', paths: [], sampledRows: 0, sampledAt: null };
  }, [cacheEntry, inMemoryPaths, filteredScopeActive, filteredInMemoryPaths]);

  useEffect(() => {
    if (!open) return;
    // M5 — pass `source` directly so 'empty' opens are reported as
    // 'empty' (a useful product signal) instead of being collapsed into
    // 'live'. The two filtered sources collapse onto the orthogonal
    // 'live'/'empty' axis so `source` stays low-cardinality; the filtered-vs-
    // whole-column distinction rides the separate `scope` prop. `scope` is
    // only attached when filters are active, keeping the whole-column event
    // shape byte-identical to before this feature.
    const reportedSource: 'cached' | 'live' | 'empty' =
      source === 'filtered-live'
        ? 'live'
        : source === 'filtered-empty'
          ? 'empty'
          : source;
    track('feature:json_peek_open', {
      engine,
      source: reportedSource,
      ...(filtersActive
        ? { scope: filteredScopeActive ? 'filtered' : 'whole-column' }
        : {}),
    });
  }, [open, engine, source, filtersActive, filteredScopeActive, track]);

  const runServerScan = useCallback(async () => {
    setScanState({ kind: 'running' });
    track('feature:json_peek_scan_more', {
      engine,
      ...(filtersActive
        ? { scope: filteredScopeActive ? 'filtered' : 'whole-column' }
        : {}),
    });
    // In filtered scope the parsed rows feed the ephemeral filtered store
    // (keyed by signature) and MUST NOT be flushed to MetaInfo; whole-column
    // scope keeps the existing persist-and-invalidate pipeline.
    const ingest = (value: unknown) => {
      if (filteredScopeActive && scanSignature) {
        pushFilteredSample(columnKey, scanSignature, value);
      } else {
        pushSample(columnKey, value);
      }
    };
    try {
      const sql = buildJsonScanSql({
        engine,
        schema,
        table,
        column,
        declaredOnText,
        pkColumns,
        limit: JSON_SERVER_SCAN_LIMIT,
        ...(filteredScopeActive ? { scanFilters, scanColumnTypeOf } : {}),
      });
      const result = await executeQueryV2({
        connectionId,
        database,
        sql,
        executionId: createExecutionId(`json-peek-${column}`),
      });
      if (!result.success) {
        setScanState({
          kind: 'error',
          message: result.error?.message ?? 'Scan failed',
        });
        return;
      }
      const rs = result.resultSets?.[0];
      if (!rs) {
        setScanState({ kind: 'idle' });
        return;
      }
      const colKey = rs.columns[0]?.id ?? rs.columns[0]?.name ?? column;
      for (const row of rs.rows) {
        const raw = (row as Record<string, unknown>)[colKey];
        if (raw === null || raw === undefined) continue;
        if (typeof raw === 'object') {
          ingest(raw);
          continue;
        }
        if (typeof raw === 'string' && looksLikeJsonString(raw)) {
          try {
            ingest(JSON.parse(raw));
          } catch {
            // ignore — fall through
          }
        }
      }
      setScanState({ kind: 'idle' });
      // Filtered scans are session-only — skip the persist-and-invalidate
      // tail entirely so filtered samples never reach the on-disk structure
      // cache (invariant: filtered aggregates must not skew MetaInfo).
      if (!filteredScopeActive) {
        // #5 — flush the aggregator synchronously so the persisted cache
        // entry is in place before we invalidate MetaInfo. The previous
        // `setTimeout(..., 100)` fired before the 5s debounced idle-callback
        // flush, briefly surfacing stale cache paths after a fresh scan.
        try {
          await flushNow(keyId(columnKey));
        } catch {
          // flushNow swallows upsert errors itself; this catch is just a
          // safety net so the UI stays alive.
        }
        await invalidateMetaInfo(connectionId, database);
      }
    } catch (e) {
      setScanState({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Scan failed',
      });
    }
  }, [
    column,
    columnKey,
    connectionId,
    database,
    declaredOnText,
    engine,
    filteredScopeActive,
    filtersActive,
    flushNow,
    invalidateMetaInfo,
    pkColumns,
    pushFilteredSample,
    pushSample,
    scanColumnTypeOf,
    scanFilters,
    scanSignature,
    schema,
    table,
    track,
  ]);

  const handlePickPath = useCallback(
    (path: string, type: JsonStructureCachePathType | undefined) => {
      // Existing path-change wire stays so back-compat callers (and the
      // unit tests asserting `onPathChange` fires on tree-picks) keep
      // working. Hosts that want type-aware sub-op defaults wire
      // `onTreePick` in addition.
      onPathChange(path);
      if (onTreePick) onTreePick(path, type);
      setOpen(false);
      // Move focus forward (value input or Apply) on the next tick so the
      // popover-close transition doesn't snatch focus back. Parents own
      // the actual ref; we just signal "pick is committed".
      if (onPickPathComplete) {
        setTimeout(onPickPathComplete, 0);
      }
    },
    [onPathChange, onPickPathComplete, onTreePick],
  );

  const handlePickDocumentScope = useCallback(
    (sub: 'is_valid' | 'is_not_valid' | 'is_empty') => {
      // Single update path: the chip's `onPickDocumentScope` handler
      // sets `jsonSubOperator` AND clears `condition.path` in one call.
      // We must NOT also fire `onPathChange('')` here — both callbacks
      // route through the parent's `handleConditionChange`, which closes
      // over a render-time `filterGroup`. A second call from the same
      // event handler would see the stale committed condition (without
      // the sub-op update queued in call 1) and overwrite it, leaving
      // the chip with the old sub-op. The popover's controlled
      // `pathValue` re-renders correctly on its own when the parent
      // commits the new condition.
      if (onPickDocumentScope) onPickDocumentScope(sub);
      setOpen(false);
      if (onPickPathComplete) {
        setTimeout(onPickPathComplete, 0);
      }
    },
    [onPickDocumentScope, onPickPathComplete],
  );

  // Tree built from the flat `paths` list — see `buildPathTree`. Memoised so
  // toggle interactions don't rebuild on every render.
  const pathTree = useMemo(() => buildPathTree(paths), [paths]);

  // Collapse state for the tree — set of paths the user has collapsed. Empty
  // by default (everything expanded) so the popover reads identically to the
  // old flat list at first open. Resets when the source paths change (a new
  // scan should give a clean slate).
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setCollapsedPaths(new Set());
  }, [pathTree]);
  const toggleCollapsed = useCallback((path: string) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          id={triggerId}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={scope === 'whole-column' ? 'JSON scope — whole column' : 'JSON path'}
          aria-invalid={pathMalformed || undefined}
          disabled={disabled}
          data-testid={triggerTestId}
          data-scope={scope}
          title={
            scope === 'whole-column'
              ? 'Whole column — click to pick a JSON property instead'
              : pathHint
                ? `${pathValue || 'JSON path'} — ${JSON_PATH_HINT_TEXT[pathHint]}`
                : pathValue || 'JSON path'
          }
          className={cn(
            'inline-flex h-6 w-auto min-w-[140px] max-w-56 items-center justify-between gap-1 rounded-md border border-input bg-background px-2 text-xs font-mono shadow-xs hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            // In whole-column mode the label isn't a JSONPath — drop the mono
            // family so it visually reads as a state, not a typed string.
            scope === 'whole-column' && 'font-sans',
            pathMalformed && 'border-destructive/60 focus-visible:ring-destructive/40',
          )}
        >
          {scope === 'whole-column' ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <Braces
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <span
                translate="no"
                className="truncate text-left text-foreground"
                data-testid="json-peek-trigger-whole-column-label"
              >
                whole column
              </span>
            </span>
          ) : (
            <span
              translate="no"
              className={cn(
                'truncate text-left min-w-0',
                !pathValue && 'text-muted-foreground font-sans',
              )}
            >
              {pathValue || 'e.g. $.path…'}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-2"
        align="start"
        portalled={false}
        data-testid="json-peek-popover"
        onOpenAutoFocus={(e) => {
          // Land focus inside the freetext input rather than the first
          // discovered-path button — typing should be the default action
          // when the popover opens.
          e.preventDefault();
          freetextInputRef.current?.focus();
          freetextInputRef.current?.select();
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          // Radix's @radix-ui/react-use-escape-keydown registers its listener on
          // document with { capture: true }, so it runs BEFORE this handler's
          // bubble-phase event. By the time Escape reaches here, Radix has already
          // called preventDefault() and invoked onOpenChange(false). Presence keeps
          // this content mounted through its exit animation (the shadcn
          // PopoverContent's data-state=closed animate-out classes), and FocusScope
          // restores focus after that unmount, so the popover is still mounted
          // while this handler runs. (The app's prefers-reduced-motion block in
          // index.css targets the plain `.animate-out` class, not the
          // variant-prefixed one this content uses, so it does not shorten that
          // window.)
          //
          // The setOpen(false) here is a harmless defensive no-op. The real purpose
          // is stopping this event from bubbling further up the React tree — without
          // preventDefault + stopPropagation, the Escape would reach the chip's own
          // Escape handlers (handleJsonKeyDown / handleChipRootKeyDown in FilterChip),
          // which would cancel the user's in-progress JSON filter draft.
          //
          // `portalled={false}` renders this popover content inside the chip's
          // React tree, so they share the same bubble-phase listeners. Stopping
          // propagation here means a second Escape (popover already closed) still
          // reaches the chip and cancels the draft, as it always did before.
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }}
        onFocusOutside={(e) => {
          // Keep the popover open when focus merely moves out of it — e.g. when
          // the grid's async row-load / ephemeral filtered-aggregate settle
          // triggers a background re-render that pulls focus off the freetext
          // input. Without this guard radix's default dismiss fires
          // `onOpenChange(false)` and the popover vanishes mid-interaction (the
          // control the user was about to click detaches). Genuine dismissals —
          // a pointer-down outside, Escape, Enter, or the close button — still
          // close it (only the focus-out path is suppressed).
          e.preventDefault();
        }}
      >
        <div className="pb-2 border-b border-border">
          <Input
            ref={freetextInputRef}
            type="text"
            value={pathValue}
            onChange={(e) => onPathChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setOpen(false);
                if (onPickPathComplete) setTimeout(onPickPathComplete, 0);
              }
            }}
            placeholder="e.g. $.path…"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            translate="no"
            aria-label="JSON path"
            aria-invalid={pathMalformed || undefined}
            className={cn(
              'h-7 px-2 text-xs font-mono',
              pathMalformed && 'border-destructive/60 focus-visible:ring-destructive/40',
            )}
            data-testid={inputTestId}
          />
          {pathHint ? (
            <span
              className="mt-1 flex items-center gap-1 text-[10px] text-destructive"
              data-testid="json-path-malformed-hint"
            >
              <span className="pointer-events-none">{JSON_PATH_HINT_TEXT[pathHint]}</span>
              {pathHint === 'mssql-low-compat-wildcard' && onRecheckCompat && (
                <button
                  type="button"
                  disabled={recheckCompatFetching}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRecheckCompat();
                  }}
                  className="underline hover:no-underline disabled:opacity-50"
                  data-testid="json-path-recheck-compat"
                >
                  {recheckCompatFetching ? 'Checking…' : 'Recheck'}
                </button>
              )}
            </span>
          ) : showWildcardHint ? (
            <span
              className="mt-1 block text-[10px] text-muted-foreground pointer-events-none"
              data-testid="json-path-tip"
            >
              Use <span className="font-mono">[*]</span> to match array elements.
            </span>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 py-2 border-b border-border">
          <div className="text-xs text-muted-foreground" data-testid="json-peek-header">
            {source === 'filtered-live'
              ? `Matching current filters · ${sampledRows.toLocaleString()} rows`
              : source === 'filtered-empty'
                ? 'No paths for current filters yet'
                : source === 'cached' && sampledAt
                  ? `Last scanned ${formatRelativeTime(sampledAt)} · ${sampledRows.toLocaleString()} rows${noOrderHint ? ' · (no order)' : ''}`
                  : source === 'live'
                    ? `Live (this session) · ${sampledRows.toLocaleString()} rows${noOrderHint ? ' · (no order)' : ''}`
                    : 'No paths discovered yet'}
          </div>
          <Button
            type="button"
            variant={source === 'empty' ? 'default' : 'ghost'}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={runServerScan}
            disabled={scanState.kind === 'running'}
            data-testid="json-peek-scan-more"
          >
            {scanState.kind === 'running' ? (
              <RandomDotmSpinner size={12} className="mr-1" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" />
            )}
            {filteredScopeActive ? (
              `Scan ${JSON_SERVER_SCAN_LIMIT.toLocaleString()} matching rows`
            ) : (
              <>Scan&nbsp;{JSON_SERVER_SCAN_LIMIT.toLocaleString()} from Server</>
            )}
          </Button>
        </div>
        {filtersActive && (
          <button
            type="button"
            data-testid="json-peek-scope-toggle"
            onClick={() => {
              const next = scanScope === 'filtered' ? 'whole-column' : 'filtered';
              setScanScope(next);
              track('feature:json_peek_scope_toggle', { engine, scope: next });
            }}
            className="mt-1 text-[10px] text-muted-foreground underline hover:no-underline text-left"
          >
            {scanScope === 'filtered'
              ? 'Scan whole column instead'
              : 'Match current filters'}
          </button>
        )}
        {scanState.kind === 'error' && (
          <div
            className="mt-2 text-xs text-destructive"
            data-testid="json-peek-scan-error"
          >
            {scanState.message}
          </div>
        )}
        <div
          className="mt-2 max-h-72 overflow-y-auto overscroll-contain"
          data-testid="json-peek-path-list"
        >
          {paths.length === 0 ? (
            <div
              className="text-xs text-muted-foreground py-4 text-center"
              data-testid="json-peek-empty"
            >
              {scanState.kind === 'running'
                ? 'Scanning…'
                : source === 'filtered-empty'
                  ? 'No rows sampled for the current filters — run a scan.'
                  : sampledRows > 0
                    ? // Sampled rows but no paths means every value was a
                      // root-level primitive (string / number / null). Arrays
                      // are walked now and contribute `$[*]` paths, so this
                      // branch only fires for true primitive columns.
                      'No paths found — sampled values were primitives, not JSON objects or arrays.'
                    : 'Browse this column or click "Scan from Server" to discover paths.'}
            </div>
          ) : (
            <PeekPathTree
              tree={pathTree}
              collapsed={collapsedPaths}
              onToggle={toggleCollapsed}
              onPick={handlePickPath}
              peekCtx={{
                connectionId,
                database,
                schema,
                table,
                column,
                engine,
                enumColumns: enumColumnsForColumn,
              }}
            />
          )}
        </div>
        {onPickDocumentScope && (
          <div className="mt-2 pt-2 border-t border-border flex items-center gap-1">
            <button
              type="button"
              onClick={() =>
                handlePickDocumentScope(
                  // Default whole-column sub-op when picked via the
                  // footer link. The chip ignores this when already in
                  // a document-level sub-op (round-trip → round-trip is
                  // a no-op for the dropdown).
                  declaredOnText ? 'is_valid' : 'is_empty',
                )
              }
              aria-current={scope === 'whole-column' ? 'true' : undefined}
              data-testid="json-peek-whole-column-link"
              data-current={scope === 'whole-column' ? 'true' : 'false'}
              className={cn(
                'flex-1 flex items-center gap-1.5 px-2 py-1 text-xs rounded text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                scope === 'whole-column'
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground',
              )}
            >
              {scope === 'whole-column' ? (
                <Check
                  className="h-3 w-3 shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <span className="inline-block h-3 w-3 shrink-0" aria-hidden="true" />
              )}
              <span>
                {scope === 'whole-column'
                  ? 'Filtering on the whole column'
                  : 'Filter on the whole column instead…'}
              </span>
            </button>
            <button
              type="button"
              data-testid="json-peek-column-details"
              title="Open JSON column details"
              aria-label="Open JSON column details"
              onClick={(e) => {
                e.stopPropagation();
                openJsonInspectorDialog({
                  connectionId,
                  database,
                  schema,
                  table,
                  column,
                  engine,
                  source: 'json-peek-footer',
                });
              }}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
            >
              <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

type PeekPathTreeProps = {
  tree: PathTreeNode;
  collapsed: ReadonlySet<string>;
  onToggle: (path: string) => void;
  onPick: (path: string, type: JsonStructureCachePathType | undefined) => void;
  peekCtx?: PeekCtx;
};

/**
 * Renders the path tree as a flat list of rows with indent-by-depth.
 * Interior nodes get an inline chevron toggle (separate `<button>` with
 * no `data-testid`, so existing `button[data-testid]` queries against
 * the list keep returning only path-pick rows). Leaves get a fixed-
 * width spacer in place of the chevron so the segment column lines up.
 */
function PeekPathTree({ tree, collapsed, onToggle, onPick, peekCtx }: PeekPathTreeProps) {
  // The root `$` itself isn't rendered — only its children. That keeps
  // the visible list parallel to the old flat-list output for shallow
  // JSON, and avoids a top-level `$` row testid that would shift the
  // existing flat-list assertions.
  return (
    <>
      {tree.children.map((c) => (
        <PeekPathRow
          key={c.path}
          node={c}
          depth={0}
          collapsed={collapsed}
          onToggle={onToggle}
          onPick={onPick}
          peekCtx={peekCtx}
        />
      ))}
    </>
  );
}

type PeekPathRowProps = {
  node: PathTreeNode;
  depth: number;
  collapsed: ReadonlySet<string>;
  onToggle: (path: string) => void;
  onPick: (path: string, type: JsonStructureCachePathType | undefined) => void;
  peekCtx?: PeekCtx;
};

function PeekPathRow({ node, depth, collapsed, onToggle, onPick, peekCtx }: PeekPathRowProps) {
  const hasChildren = node.children.length > 0;
  const isCollapsed = hasChildren && collapsed.has(node.path);
  // Indent is rendered as `style.paddingLeft` so the row's hover-bg fills
  // the full popover width — pure tailwind utilities would have to bake
  // every depth class in.
  const indentPx = depth * 12;
  const TypeIcon = node.type ? TYPE_ICONS[node.type] : undefined;

  // Enum affordance state per leaf / array node.
  //  - declared (blue ▾)  → an EnumColumnDeclaration exists for this exact path
  //  - eligible (gray ▾?) → name passes `nameSuggestsEnum` but no declaration
  //  - none               → no inline glyph; the user can still declare via
  //                         the structure-details "Declare property as enum…"
  //                         pick-mode (covers non-eligible names).
  // We only consider scalar leaves and array nodes — object containers are
  // not enum targets.
  const stripArraySuffix = (segment: string): string =>
    segment.replace(/(\[\*\])+$/, '');
  const enumPathKind: 'scalar' | 'array' | null =
    node.type === 'array' ? 'array' : !hasChildren ? 'scalar' : null;
  const declared = peekCtx && enumPathKind
    ? findEnumDeclarationForPath(
        peekCtx.enumColumns,
        peekCtx.schema,
        peekCtx.table,
        peekCtx.column,
        node.path,
      )
    : undefined;
  const eligible = peekCtx && enumPathKind && !declared
    ? nameSuggestsEnum(stripArraySuffix(node.segment))
    : false;
  return (
    <Fragment>
      <div
        className="group flex items-stretch"
        style={{ paddingLeft: indentPx }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.path);
            }}
            className="inline-flex h-[22px] w-4 items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            aria-label={isCollapsed ? `Expand ${node.path}` : `Collapse ${node.path}`}
            aria-expanded={!isCollapsed}
            tabIndex={-1}
          >
            {isCollapsed ? (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            )}
          </button>
        ) : (
          <span className="inline-block h-[22px] w-4" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={() => onPick(node.path, node.type)}
          data-testid={`json-peek-path-${node.path}`}
          title={node.path}
          className="flex-1 flex items-center justify-between gap-2 px-1 py-1 text-xs rounded hover:bg-accent focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-left min-w-0"
        >
          <span className="flex items-center gap-1.5 min-w-0">
            {TypeIcon && node.type ? (
              <span
                title={TYPE_LABELS[node.type]}
                aria-label={TYPE_LABELS[node.type]}
                className="inline-flex shrink-0"
              >
                <TypeIcon
                  className="h-3 w-3 text-muted-foreground shrink-0"
                  aria-hidden="true"
                  data-testid={`json-peek-type-${node.type}`}
                />
              </span>
            ) : (
              <span className="inline-block h-3 w-3 shrink-0" aria-hidden="true" />
            )}
            <span
              translate="no"
              className="font-mono truncate text-foreground"
            >
              {node.segment}
            </span>
            {peekCtx && enumPathKind && declared && (
              <PathEnumGlyph
                state="declared"
                path={node.path}
                onClick={() =>
                  openEnumDetailsDialog({
                    connectionId: peekCtx.connectionId,
                    database: peekCtx.database,
                    schema: peekCtx.schema,
                    table: peekCtx.table,
                    column: peekCtx.column,
                    engine: peekCtx.engine,
                    declaredSource: declared.source,
                    source: 'json-peek',
                    path: declared.path ?? node.path,
                    pathKind: declared.pathKind ?? enumPathKind,
                  })
                }
              />
            )}
            {peekCtx && enumPathKind && !declared && eligible && (
              <PathEnumGlyph
                state="eligible"
                path={node.path}
                onClick={() =>
                  openDeclareEnumColumnDialog({
                    connectionId: peekCtx.connectionId,
                    database: peekCtx.database,
                    schema: peekCtx.schema,
                    table: peekCtx.table,
                    column: peekCtx.column,
                    engine: peekCtx.engine,
                    mode: 'eligible',
                    trigger: 'filter-chip',
                    path: node.path,
                    pathKind: enumPathKind,
                  })
                }
              />
            )}
          </span>
          <span className="flex items-center gap-2 text-muted-foreground shrink-0">
            <span className="tabular-nums text-[10px] min-w-[2.5rem] text-right">
              {node.prevalence !== undefined
                ? percentFormatter.format(node.prevalence)
                : ''}
            </span>
          </span>
        </button>
      </div>
      {hasChildren && !isCollapsed && (
        <>
          {node.children.map((child) => (
            <PeekPathRow
              key={child.path}
              node={child}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              onPick={onPick}
              peekCtx={peekCtx}
            />
          ))}
        </>
      )}
    </Fragment>
  );
}

